import React from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  useTheme,
  Chip,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material'
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Launch as LaunchIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Home as HomeIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/useAuth'

interface AdminFunction {
  id: string
  title: string
  description: string
  icon: React.ReactElement
  path: string
  status: 'Available' | 'Coming Soon' | 'Further Enhancement'
  features: string[]
}

const AdminDashboard: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { user, userPermissions, logout } = useAuth()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      await logout()
      handleClose()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const adminFunctions: AdminFunction[] = [
    {
      id: 'landing-config',
      title: 'Landing Page Configuration',
      description: 'Manage application cards, descriptions, and access permissions for the client portal landing page.',
      icon: <SettingsIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      path: '/admin/landing-config',
      status: 'Available',
      features: ['Add/Edit Application Cards', 'Set Access Permissions', 'Manage Display Order', 'Configure Descriptions', 'Icon Management']
    },
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Manage user accounts, permissions, and access control for the Falcon MM Web App.',
      icon: <PeopleIcon sx={{ fontSize: 48, color: theme.palette.secondary.main }} />,
      path: '/admin/users',
      status: 'Further Enhancement',
      features: ['User Account Management', 'Permission Assignment', 'Group Management', 'Access Control', 'User Activity Logs']
    },
    {
      id: 'system-config',
      title: 'System Configuration',
      description: 'Configure system-wide settings, security policies, and application behavior.',
      icon: <SecurityIcon sx={{ fontSize: 48, color: theme.palette.warning.main }} />,
      path: '/admin/system-config',
      status: 'Further Enhancement',
      features: ['Security Policies', 'System Settings', 'API Configuration', 'Backup Settings', 'Audit Configuration']
    },
    {
      id: 'analytics',
      title: 'Admin Analytics',
      description: 'View system usage analytics, user activity reports, and performance metrics.',
      icon: <AssessmentIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />,
      path: '/admin/analytics',
      status: 'Further Enhancement',
      features: ['Usage Analytics', 'User Activity Reports', 'Performance Metrics', 'System Health', 'Custom Reports']
    }
  ]

  const handleFunctionAccess = (adminFunction: AdminFunction) => {
    if (adminFunction.status === 'Available') {
      navigate(adminFunction.path)
    }
  }

  if (!userPermissions?.isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error" textAlign="center">
          Access Denied
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ mt: 2 }}>
          You do not have administrator privileges to access this page.
        </Typography>
      </Container>
    )
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Falcon MM Administration
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none' }}
            >
              Back to Portal
            </Button>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.name || user?.username || 'Admin'}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                {(user?.name || user?.username || 'A').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Administration Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Manage system configuration, users, and application settings
            </Typography>
            
            {/* Admin Status */}
            <Box sx={{ mt: 2 }}>
              <Chip label="ADMINISTRATOR" color="error" size="small" />
            </Box>
          </Box>

          {/* Admin Functions Grid */}
          <Grid container spacing={4}>
            {adminFunctions.map((func) => {
              const isAvailable = func.status === 'Available'
              
              return (
                <Grid item xs={12} md={6} key={func.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': isAvailable ? {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      } : {},
                      opacity: isAvailable ? 1 : 0.7,
                      border: isAvailable ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0'
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2 }}>
                          {func.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {func.title}
                          </Typography>
                          <Chip
                            label={func.status}
                            size="small"
                            color={func.status === 'Available' ? 'success' : 'default'}
                            sx={{ mb: 2 }}
                          />
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" paragraph>
                        {func.description}
                      </Typography>
                    </CardContent>
                    
                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        variant={isAvailable ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        disabled={!isAvailable}
                        startIcon={isAvailable ? <LaunchIcon /> : <AssessmentIcon />}
                        onClick={() => handleFunctionAccess(func)}
                        sx={{ textTransform: 'none' }}
                      >
                        {isAvailable ? 'Access Function' : 'Further Enhancement'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {/* Quick Actions */}
          <Box sx={{ mt: 6, p: 3, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate('/admin/landing-config')}
                  sx={{ textTransform: 'none' }}
                >
                  Edit Landing Page
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  startIcon={<PeopleIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Manage Users (Further Enhancement)
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DashboardIcon />}
                  onClick={() => navigate('/')}
                  sx={{ textTransform: 'none' }}
                >
                  Back to Portal
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    )
}

export default AdminDashboard
